import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { plantApi } from '../lib/api';
import type { PlantSummary } from '../lib/types';
import { useAuth } from './AuthContext';

interface PlantState {
  plants: PlantSummary[];
  currentPlant: PlantSummary | null;
  setCurrentPlantId: (id: string) => void;
  loading: boolean;
  reload: () => Promise<void>;
}

const PlantContext = createContext<PlantState | null>(null);

const STORAGE_KEY = 'permitguard.currentPlantId';

export function PlantProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [plants, setPlants] = useState<PlantSummary[]>([]);
  const [currentPlantId, setCurrentPlantIdState] = useState<string | null>(() => localStorage.getItem(STORAGE_KEY));
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await plantApi.list();
      setPlants(res.plants);
      setCurrentPlantIdState((prev) => {
        if (prev && res.plants.some((p) => p.id === prev)) return prev;
        return res.plants[0]?.id ?? null;
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      void load();
    } else {
      setPlants([]);
      setCurrentPlantIdState(null);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const setCurrentPlantId = (id: string) => {
    setCurrentPlantIdState(id);
    localStorage.setItem(STORAGE_KEY, id);
  };

  const currentPlant = useMemo(() => plants.find((p) => p.id === currentPlantId) ?? null, [plants, currentPlantId]);

  const value = useMemo(() => ({ plants, currentPlant, setCurrentPlantId, loading, reload: load }), [plants, currentPlant, loading]);

  return <PlantContext.Provider value={value}>{children}</PlantContext.Provider>;
}

export function usePlant(): PlantState {
  const ctx = useContext(PlantContext);
  if (!ctx) throw new Error('usePlant must be used within PlantProvider');
  return ctx;
}
